import React, { AriaAttributes, useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Props as IconProps } from '@wix/da-ds/pkg/Icons/IconWrap';
import NetworkBarGroupLink from '../NetworkBarGroupLink';
import Button from '../../../Button';
import GroupLinkLoader from '../GroupLinkLoader';
import NetworkBarItemGroup from '../NetworkBarItemGroup';
import { useNormalizedUserStream } from '../../../utils/hooks/useStream';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { NetworkBarStreamTypes } from '../../redux/types';
import { BiData, NetworkbarSectionExpandCollapseEvent } from '@wix/da-bi/pkg/events';
import s from './FoldableGroupList.scss';

export interface Props {
  streamId: string;
  title?: string;
  icon: React.ComponentType<IconProps>;
  isCollapsed?: boolean;
  selectedItem?: string | number;
  tabIndex?: number;
  className?: string;
  selectedItemClassName?: string;
  sectionType: NetworkBarStreamTypes;
  isFolded?: boolean;
  toggleAriaLabel?: AriaAttributes['aria-label'];
  prefix?: React.ReactNode;
  onFoldToggle: (section: NetworkBarStreamTypes, isFoldedNow: boolean) => void;
}

const FoldableGroupList: React.FC<React.PropsWithChildren<Props>> = ({
  streamId,
  tabIndex,
  isCollapsed,
  selectedItem,
  selectedItemClassName,
  title,
  icon,
  isFolded,
  toggleAriaLabel,
  onFoldToggle,
  className,
  prefix,
  sectionType,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const {
    items: groups,
    hasMore,
    isFetching,
    fetchNext,
    errorMessage,
  } = useNormalizedUserStream(streamId);

  if (!isFetching && !groups.length && (!hasMore || errorMessage)) {
    return null;
  }

  const hasNewContent = groups.some(group => group.networkbar?.hasNewContent);
  return (
    <NetworkBarItemGroup
      title={title}
      icon={icon}
      isCollapsed={isCollapsed}
      className={className}
      hasNewContent={hasNewContent}
      withFolding
      isFolded={isFolded}
      toggleAriaLabel={toggleAriaLabel}
      onFoldToggle={() => onFoldToggle(sectionType, !isFolded)}
      biData={BiData<NetworkbarSectionExpandCollapseEvent>({
        evid: 164,
        is_expanding: isFolded ? 1 : 0,
        indicator: hasNewContent ? 1 : 0,
      })}
    >
      <ul>
        {prefix && <li key="newgroup">{prefix}</li>}
        {groups.map((group, index) => (
          <li key={group.userId}>
            <NetworkBarGroupLink
              group={group}
              tabIndex={tabIndex}
              rank={index}
              isCollapsed={!isMobile && isCollapsed}
              isSelected={selectedItem === group.userId}
              className={classnames(selectedItem === group.userId && selectedItemClassName)}
            />
          </li>
        ))}
        {isFetching && (
          <>
            <li>
              <GroupLinkLoader isCollapsed={isCollapsed} />
            </li>
            <li>
              <GroupLinkLoader isCollapsed={isCollapsed} />
            </li>
            <li>
              <GroupLinkLoader isCollapsed={isCollapsed} />
            </li>
          </>
        )}
      </ul>
      {hasMore && !isFetching && !isCollapsed && (
        <Button view="b33" className={s['more-button']} onClick={fetchNext} tabIndex={tabIndex}>
          {t('networkBar.groups.showMore')}
        </Button>
      )}
    </NetworkBarItemGroup>
  );
};
FoldableGroupList.displayName = 'FoldableGroupList';

export default FoldableGroupList;
